/* ------------------------------------------------------------------
   shipping-estimate.js — 商品页「测算运费」区块
   数据来源：Storefront API Cart.deliveryGroups（Shopify 按后台运费模板
   用「真实变体 + 真实数量 + 访客国家」实算，与结账页同源）。

   UI 结构（各行各司其职，互不干扰）：
     第 1 行  国家/地区     ← 无国旗
     第 2 行  免邮门槛      ← 来自区块设置里的预设表，本地数据，瞬间显示、
                              切换国家时立即更新，不参与加载、不会被计算结果覆盖
     第 3 行  实时运费      ← 加载时显示 spinner + "Calculating shipping…"；
                              成功后显示金额；不可售显示无法配送；
                              超过 60s 未返回则显示兜底文案（fail_note），
                              迟到的结果不会再覆盖它

   v7 变更（相对 v6）—— 为「其他仓库补库存」做的适配，核心是【判定权全部交给接口】：
     a. 删除「按国家硬编码可配送与否」的门卫。原来 AT/BE/DK/FI/NZ/JE/LT/VA/MO
        被写死为不可配送，但 2026-09-29 实测发现：这些国家能不能发，
        【取决于商品自己】而不是取决于国家 —— 从 Warehouse-GZZF 发货的商品
        这 9 国全部正常返回运费（$10），从 Warehouse-HK海运 发货的商品则不可发。
        写死国家 → GZZF 商品的顾客会被错误告知「无法配送」。
     b. 可配送判定改为两级，全部来自接口：
        ① checkAvailability()：product.availableForSale（带 @inContext 国家）
           T → 可发；F 或「商品在该国不可见」→ 不可发
        ② 实际建车结果：totalQuantity=0 → 不可发；有 deliveryOptions → 出金额
        实测 41 国 × 2 个不同仓库来源的变体，①的 T/F 与②的结果 100% 一致。
     c. 预设表现在【只负责第 1 行的免邮门槛文案】，不再当白名单用；
        下拉框列出的国家 = 预设表里的国家。访客所在国即使不在预设表里，
        也会照常探测并给出真实结果（只是没有门槛行）。
     d. 修掉「多发货组」下取 min 的问题：改为「每个发货组取最低价后求和」
        （与结账页算法一致）。实测单行购物车在 112 件、跨两个仓库时
        Shopify 仍只返回 1 个组，所以这是防御性修正，正常情况无影响。

   v6 变更（相对 v5）：
     b. 国家下拉框只列出可配送的国家；当前访客所在国若不在此列，
        则单独插到最前，保证下拉框显示的永远与第 1 行的国家一致
     c. 下拉框按国家【英文名】排序（原来按国家码排）

   v5 变更（相对 v4，均经过实测，勿改）：
     a. 去掉「配送时效」行 —— 不做时效显示了
     b. JP/HK/MY/PH/MX 五国的运费计算【必须】在地址里带 province（省/州），
        否则 deliveryGroups 永远为空、前端只能显示兜底文案。
        实测占位值（Shopify 的省份名必须精确匹配）：
          JP=Tokyo / HK=Kowloon / MY=Kuala Lumpur / PH=Metro Manila / MX=Jalisco
        （JP=Osaka、MY=Selangor、PH=Metro Manila、HK=Hong Kong Island 也可）
        其余国家（US/CA/DE…）带不带 province 都能正常计算，故不加。
     c. 新增「预设表门卫」：区块设置预设表里没有的国家直接显示「无法配送」，
        不再发请求。因为 CH（不属于任何市场）等国的接口表现是
        "cart 正常但 deliveryGroups 永远为空"，这类国家若不拦住，
        用户看到的会是 60 秒兜底文案而非明确的「无法配送」。

   v4 变更（保留）：
     a. 去掉国家名前的国旗 emoji
     b. 无法配送文案统一为「…to your country or region.」——
        后台区块设置若仍是旧文案（…to your country.）会自动归一化
     c. 样式（边框 / 底色 / 选择框外观）全部由 Liquid 承担，JS 未改动样式

   关键实现约束（均经过实测，勿改）：
   1. 必须同时传 countryCode + 占位邮编，否则 deliveryGroups 永远为空
   2. 国家必须在 cartCreate 那一刻传入，否则 Shopify 按请求 IP 定国，
      商品若在该市场不可售，加购的行会被直接丢弃
   3. deliveryGroups 首次查询可能为空（异步计算），需 300ms 轮询兜底
   4. 不要查询 quantityAvailable 字段（需要额外 scope，会 403）
   5. 每个浏览器会话只建 1 个估算购物车（sessionStorage 复用），
      不写 email/phone，不会触发弃单邮件
   6. 算运费前先做「可得性预检」——见 checkAvailability()。
      若商品未发布到访客所在国家对应的 Shopify 市场，Shopify 会在加购时
      静默丢弃行项目（userErrors 为空但 totalQuantity=0）。预检把它提前到
      显示价格之前，避免"先闪出数字、再变成不可配送"
------------------------------------------------------------------- */
(function () {
  'use strict';

  var CART_ID_KEY = 'bse:cartId';
  var CART_COUNTRY_KEY = 'bse:cartCountry';
  var OVERRIDE_COUNTRY_KEY = 'bse:country';
  var CACHE_PREFIX = 'bse:cache5:';
  var AVAIL_PREFIX = 'bse:avl:';
  var CACHE_TTL = 30 * 60 * 1000;      // 结果缓存 30 分钟
  var CALC_TIMEOUT_MS = 60 * 1000;     // 计算超时：60 秒后显示兜底文案
  var DEFAULT_CHECKOUT_NOTE = 'Final shipping is calculated at checkout.';

  /* v4：无法配送文案。
     主题里保存的区块设置可能还是旧文案（没有 "or region"），
     这里做一次归一化，保证新文案在任何情况下都生效；
     若你在后台把它改成了别的写法，则原样使用你的写法。 */
  var CANNOT_SHIP_LEGACY = "Sorry, we currently can't ship this item to your country.";
  var CANNOT_SHIP_TEXT = "Sorry, we currently can't ship this item to your country or region.";

  function normalizeCannotShip(s) {
    if (!s || !String(s).trim() || String(s).trim() === CANNOT_SHIP_LEGACY) {
      return CANNOT_SHIP_TEXT;
    }
    return s;
  }

  /* 国家基础信息表。只提供「构造一个合法收货地址」所需的三样东西：
     name ：MailingAddressInput.country 需要的国家英文名
     zip  ：占位邮编——店铺运费模板没有按邮编/州分档的费率，不影响价格
     prov ：占位省/州。仅 JP/HK/MY/PH/MX 需要（不带则运费恒为空，见文件头 v5-b）；
            名字必须与 Shopify 的省份名精确一致

   ⚠️ v7 起，这里【不再】包含"某国能不能发"的判断。
      能不能发由两个接口信号决定（见文件头 v7-b），因为这个答案随商品而变：
      同一个国家，从 Warehouse-GZZF 发货的商品能发、从 Warehouse-HK海运 发货的不能发。

   ⚠️ 两种「算不出运费」是【不同原因】，排查时先看 totalQuantity：
     ① totalQuantity = 0 → 商品行被静默丢弃，未发布到覆盖该国的市场
          （userErrors 为空。实测 CN/AR/BR/CL/PE 恒为此种）
     ② totalQuantity = 1 但 deliveryGroups 为空 → 地址不完整，补 province
          （实测 JP/HK/MY/PH/MX；另 CH 属"不在任何市场"，补省州也无效）

   ⚠️ 下拉框列出的国家 = 区块设置里预设表的国家。
      名单取自「后台市场区域 ∩ 后台运费模板覆盖」= 34 国（2026-09-29 核对）。
      访客所在国即使不在预设表里，也会照常探测并给出真实结果（只是没有门槛行）。 */
  var COUNTRIES = {
    US: { name: 'United States',  zip: '10001'     },
    CA: { name: 'Canada',         zip: 'M5H 2N2'   },
    MX: { name: 'Mexico',         zip: '06600',     prov: 'Jalisco' },
    GB: { name: 'United Kingdom', zip: 'SW1A 1AA'  },
    DE: { name: 'Germany',        zip: '10117'     },
    FR: { name: 'France',         zip: '75001'     },
    AU: { name: 'Australia',      zip: '2000'      },
    ES: { name: 'Spain',          zip: '28001'     },
    IT: { name: 'Italy',          zip: '00100'     },
    IE: { name: 'Ireland',        zip: 'D02 X285'  },
    NL: { name: 'Netherlands',    zip: '1011AC'    },
    SE: { name: 'Sweden',         zip: '11120'     },
    GR: { name: 'Greece',         zip: '10431'     },
    IS: { name: 'Iceland',        zip: '101'       },
    NO: { name: 'Norway',         zip: '0150'      },
    PL: { name: 'Poland',         zip: '00-001'    },
    DK: { name: 'Denmark',        zip: '1050'      },
    FI: { name: 'Finland',        zip: '00100'     },
    AT: { name: 'Austria',        zip: '1010'      },
    BE: { name: 'Belgium',        zip: '1000'      },
    JE: { name: 'Jersey',         zip: 'JE2 4WA'   },
    LT: { name: 'Lithuania',      zip: '01103'     },
    NZ: { name: 'New Zealand',    zip: '6011'      },
    VA: { name: 'Vatican City',   zip: '00120'     },
    HK: { name: 'Hong Kong SAR',  zip: '999077',    prov: 'Kowloon' },
    JP: { name: 'Japan',          zip: '100-0001',  prov: 'Tokyo' },
    KR: { name: 'South Korea',    zip: '04524'     },
    MY: { name: 'Malaysia',       zip: '50000',     prov: 'Kuala Lumpur' },
    SG: { name: 'Singapore',      zip: '018956'    },
    TW: { name: 'Taiwan',         zip: '100'       },
    MO: { name: 'Macao SAR',      zip: '999078'    },
    PH: { name: 'Philippines',    zip: '1000',      prov: 'Metro Manila' },
    TH: { name: 'Thailand',       zip: '10110'     },
    VN: { name: 'Vietnam',        zip: '100000'    },
    /* 下面几个国家有市场、但不在任何运费分区里（或不属于任何市场），
       目前不可配送。保留条目只是为了「访客来自这些国家时能显示国家名」，
       真正结论仍由接口给（实测全部为不可发）。 */
    CH: { name: 'Switzerland',    zip: '8001'      },
    PT: { name: 'Portugal',       zip: '1250-096'  },
    CN: { name: 'China',          zip: '100000'    },
    AR: { name: 'Argentina',      zip: 'C1425'     },
    BR: { name: 'Brazil',         zip: '01001-000' },
    CL: { name: 'Chile',          zip: '8320000'   },
    PE: { name: 'Peru',           zip: '15001'     }
  };

  function ssGet(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function fmt(amount, currency) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency }).format(amount);
    } catch (e) {
      return currency + ' ' + amount.toFixed(2);
    }
  }

  /* 预设表格式：每行「国家码:起价:免邮门槛」（USD）
     起价（start）现已不再用于展示，保留解析以便兼容旧配置；
     门槛（threshold）用于第 1 行的免邮门槛文案。
     v5：表本身同时是「可配送国家名单」——不在表里的国家直接按无法配送处理。 */
  function parsePreset(raw) {
    var map = {};
    if (!raw) return map;
    String(raw).split(/\r?\n/).forEach(function (line) {
      line = line.trim();
      if (!line || line.charAt(0) === '#') return;
      var p = line.split(':');
      if (p.length >= 2) {
        var start = parseFloat(p[1]);
        var threshold = p.length >= 3 ? parseFloat(p[2]) : NaN;
        if (!isNaN(start)) {
          map[p[0].toUpperCase()] = { start: start, threshold: isNaN(threshold) ? null : threshold };
        }
      }
    });
    return map;
  }

  function debounce(fn, ms) {
    var t;
    return function () { clearTimeout(t); t = setTimeout(fn, ms); };
  }

  async function gql(url, token, query, variables) {
    var res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({ query: query, variables: variables || {} })
    });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    var json = await res.json();
    if (json.errors && json.errors.length) {
      throw new Error(json.errors[0].message || 'GraphQL error');
    }
    return json.data;
  }

  function gid(variantId) { return 'gid://shopify/ProductVariant/' + variantId; }

  function initBlock(el) {
    var cfg = {
      el: el,
      apiUrl: el.dataset.apiUrl,
      token: el.dataset.token,
      sectionId: el.dataset.sectionId,
      preset: parsePreset(el.dataset.preset),
      shipToLabel: el.dataset.shipToLabel || 'Shipping to',
      estimatedLabel: el.dataset.estimatedLabel || 'Estimated shipping',
      freeWord: el.dataset.freeWord || 'Free',
      freeOver: el.dataset.freeOver || 'Free shipping over {amount}',
      failNote: el.dataset.failNote || DEFAULT_CHECKOUT_NOTE,
      cannotShip: normalizeCannotShip(el.dataset.cannotShip),
      contactLabel: el.dataset.contactLabel || 'Contact us',
      contactUrl: el.dataset.contactUrl || ''
    };

    var countryEl = el.querySelector('[data-bse-country]');
    var thresholdEl = el.querySelector('[data-bse-threshold]'); /* 第 2 行：固定门槛 */
    var bodyEl = el.querySelector('[data-bse-body]');           /* 第 3 行：实时运费 */
    var countrySel = el.querySelector('[data-bse-select]');

    /* 加载态的初始 DOM（spinner + loading_text），由 Liquid 渲染后原样留存，
       每次进入加载态都复原它，保证 spinner 结构一致 */
    var initialHtml = bodyEl ? bodyEl.innerHTML : '';

    var state = {
      variantId: el.dataset.variantId,
      country: ssGet(OVERRIDE_COUNTRY_KEY) || el.dataset.country || 'US',
      qty: readQty(),
      busy: false,
      pending: false,
      token: 0
    };

    function readQty() {
      var input = document.querySelector(
        '.section--' + cfg.sectionId + ' form[action*="/cart/add"] input[name="quantity"]');
      var v = input ? parseInt(input.value, 10) : NaN;
      return isNaN(v) || v < 1 ? 1 : v;
    }

    function countryName(cc) { return (COUNTRIES[cc] && COUNTRIES[cc].name) || cc; }

    function renderCountry() {
      countryEl.textContent = cfg.shipToLabel + ' ' + countryName(state.country);
    }

    /* 下拉框要列出哪些国家（v7-c）：区块设置预设表里的国家。
       ⚠️ 这只是"菜单内容"，不是"能不能发"的判定 ——
       能不能发一律交给接口（checkAvailability + 建车结果），
       因为同一个国家对不同发货仓库的商品结论是不同的 */
    function inMenu(cc) {
      return !!(COUNTRIES[cc] && cfg.preset[cc]);
    }

    function buildSelect() {
      if (!countrySel) return;
      var list = Object.keys(COUNTRIES).filter(inMenu)
        .sort(function (a, b) { return countryName(a).localeCompare(countryName(b)); });

      /* 当前访客所在国若不在可配送名单里，也给它一个选项并放到最前，
         否则下拉框会显示名单里的第一个国家，与第 1 行的国家对不上 */
      if (list.indexOf(state.country) === -1) list.unshift(state.country);

      var frag = document.createDocumentFragment();
      list.forEach(function (cc) {
        var opt = document.createElement('option');
        opt.value = cc;
        opt.textContent = countryName(cc);
        if (cc === state.country) opt.selected = true;
        frag.appendChild(opt);
      });
      countrySel.appendChild(frag);
    }

    /* ---------- 第 2 行：免邮门槛（固定，不随计算变化） ---------- */

    function hideThreshold() {
      if (!thresholdEl) return;
      thresholdEl.textContent = '';
      thresholdEl.hidden = true;
    }

    function renderThreshold() {
      if (!thresholdEl) return;
      var c = COUNTRIES[state.country];
      var p = cfg.preset[state.country];
      var txt = '';
      if (c && p) {
        if (p.threshold && p.threshold > 0) {
          txt = cfg.freeOver.replace('{amount}', fmt(p.threshold, 'USD'));
        } else if (p.start === 0) {
          txt = cfg.freeWord; /* 该分区本就免费 */
        }
      }
      if (txt) {
        thresholdEl.textContent = txt;
        thresholdEl.hidden = false;
      } else {
        hideThreshold();
      }
    }

    /* ---------- 第 3 行：三种状态 ---------- */

    /* 加载中：spinner + 提示文字。
       此处刻意不显示门槛行——可售性尚未确认，若先显示门槛、随后又变成
       "无法配送"，就是一句话自相矛盾。门槛行改由「确认可售」后显示。 */
    function showLoading() {
      hideThreshold();
      if (bodyEl) bodyEl.innerHTML = initialHtml || '';
    }

    /* 不可配送：隐藏门槛行（避免"说完门槛又说寄不到"的自相矛盾） */
    function showCannotShip() {
      hideThreshold();
      if (!bodyEl) return;
      var html = escapeHtml(cfg.cannotShip);
      if (cfg.contactUrl) {
        html += ' <a class="bse__link" href="' + escapeHtml(cfg.contactUrl) + '">' + escapeHtml(cfg.contactLabel) + '</a>';
      }
      bodyEl.innerHTML = html;
    }

    /* 兜底：取不到实时运费（超时 / 接口失败）时只说"以结账为准"，
       绝不显示预设起价——避免顾客以为那是最终运费 */
    function showCheckoutNote() {
      if (!bodyEl) return;
      bodyEl.innerHTML = '<span class="bse__text">' + escapeHtml(cfg.failNote) + '</span>';
    }

    function renderResult(r) {
      if (!bodyEl) return;
      var txt = r.amount === 0 ? cfg.freeWord : fmt(r.amount, r.currency);
      bodyEl.innerHTML = '<span class="bse__text">' + escapeHtml(cfg.estimatedLabel + ': ' + txt) + '</span>';
    }

    function readCache(key) {
      var raw = ssGet(CACHE_PREFIX + key);
      if (!raw) return null;
      try {
        var obj = JSON.parse(raw);
        if (Date.now() - obj.t > CACHE_TTL) return null;
        return obj.v;
      } catch (e) { return null; }
    }
    function writeCache(key, v) {
      ssSet(CACHE_PREFIX + key, JSON.stringify({ t: Date.now(), v: v }));
    }

    /* ---------- 可得性预检 ----------
       判断该商品在访客所在国家对应的 Shopify 市场里是否可售。
       返回 true / false；返回 null 表示探测失败（不阻断主流程）。

       为什么需要它：Shopify 对"未发布到该市场"的商品，会在 cartCreate 时
       静默丢弃行项目——userErrors 为空、但 cart.totalQuantity = 0。
       靠这个事后信号只能得到"无法配送"，而且是在显示过价格之后才变成
       这个结果，顾客会看到数字先出现再消失。

       实测：@inContext(country:) 变量形式可用，cost=2，响应约 290ms，无副作用。
       product.availableForSale 反映的是"商品在该市场是否可售"（市场发布 + 履约）。
       ------------------------------------------------------------------ */
    async function checkAvailability(cc) {
      var key = AVAIL_PREFIX + state.variantId + ':' + cc;
      var cached = readCache(key);
      if (cached !== null) return cached; /* 缓存里 false 也是有效结论 */
      try {
        var data = await gql(cfg.apiUrl, cfg.token,
          'query($id: ID!, $country: CountryCode!) @inContext(country: $country) {' +
          '  node(id: $id) { ... on ProductVariant { availableForSale product { availableForSale } } }' +
          '}',
          { id: gid(state.variantId), country: cc });
        var n = data && data.node;
        /* 变体不存在，或该商品在目标国家【根本不可见】（未发布到覆盖该国的市场）
           → 结论都是「发不了」。2026-09-29 实测 41 国 × 2 个不同仓库来源的变体：
           这里返回的 F / 不可见，与后续建车结果 100% 一致（T → 必有运费），
           所以直接当 false（而不是 null"探测失败"）用。 */
        if (!n || !n.product) { writeCache(key, false); return false; }
        var sellable = !!n.product.availableForSale;
        writeCache(key, sellable);
        return sellable;
      } catch (e) {
        if (window.console) console.warn('[shipping-estimate] availability probe failed', e);
        return null;
      }
    }

    /* ---------- Storefront API 流程 ---------- */

    /* 占位收货地址。JP/HK/MY/PH/MX 必须带 province，否则运费恒为空（v5-b） */
    function buildAddr() {
      var c = COUNTRIES[state.country];
      var addr = { country: c.name, zip: c.zip };
      if (c.prov) addr.province = c.prov;
      return addr;
    }

    async function createCart() {
      var c = COUNTRIES[state.country];
      if (!c) return null;
      var data = await gql(cfg.apiUrl, cfg.token,
        'mutation($input: CartInput!) { cartCreate(input: $input) { cart { id } userErrors { field message } } }',
        {
          input: {
            lines: [{ merchandiseId: gid(state.variantId), quantity: state.qty }],
            buyerIdentity: {
              countryCode: state.country,
              deliveryAddressPreferences: [{ deliveryAddress: buildAddr() }]
            }
          }
        });
      if (data.cartCreate.userErrors && data.cartCreate.userErrors.length) return null;
      var id = data.cartCreate.cart && data.cartCreate.cart.id;
      if (id) {
        ssSet(CART_ID_KEY, id);
        ssSet(CART_COUNTRY_KEY, state.country);
      }
      return id;
    }

    async function ensureCart() {
      var cartId = ssGet(CART_ID_KEY);
      if (!cartId) return createCart();
      try {
        var data = await gql(cfg.apiUrl, cfg.token,
          'query($cid: ID!) { cart(id: $cid) { id totalQuantity lines(first: 10) { nodes { id quantity merchandise { ... on ProductVariant { id } } } } } }',
          { cid: cartId });
        var cart = data.cart;
        if (!cart || cart.totalQuantity === 0) return createCart();

        var line = null;
        (cart.lines.nodes || []).forEach(function (l) {
          if (l.merchandise && l.merchandise.id === gid(state.variantId)) line = l;
        });

        if (!line) {
          /* 同一会话换了商品：复用购物车，替换第一行 */
          var first = cart.lines.nodes[0];
          var r1 = await gql(cfg.apiUrl, cfg.token,
            'mutation($cid: ID!, $lid: ID!, $mid: ID!, $q: Int!) { cartLinesUpdate(cartId: $cid, lines: [{ id: $lid, merchandiseId: $mid, quantity: $q }]) { userErrors { field message } } }',
            { cid: cartId, lid: first.id, mid: gid(state.variantId), q: state.qty });
          if (r1.cartLinesUpdate.userErrors.length) return createCart();
        } else if (line.quantity !== state.qty) {
          var r2 = await gql(cfg.apiUrl, cfg.token,
            'mutation($cid: ID!, $lid: ID!, $q: Int!) { cartLinesUpdate(cartId: $cid, lines: [{ id: $lid, quantity: $q }]) { userErrors { field message } } }',
            { cid: cartId, lid: line.id, q: state.qty });
          if (r2.cartLinesUpdate.userErrors.length) return createCart();
        }

        /* 访客国家与建车时不一致 → 更新 buyerIdentity（含收货地址偏好） */
        if (ssGet(CART_COUNTRY_KEY) !== state.country) {
          var c = COUNTRIES[state.country];
          if (c) {
            var addr = buildAddr();
            var r3 = await gql(cfg.apiUrl, cfg.token,
              'mutation($cid: ID!, $cc: CountryCode!, $country: String!, $zip: String, $prov: String) { cartBuyerIdentityUpdate(cartId: $cid, buyerIdentity: { countryCode: $cc, deliveryAddressPreferences: [{ deliveryAddress: { country: $country, zip: $zip, province: $prov } }] }) { userErrors { field message } } }',
              { cid: cartId, cc: state.country, country: addr.country, zip: addr.zip, prov: addr.province || null });
            /* 无论成功与否都记录，避免每次渲染都重试 */
            ssSet(CART_COUNTRY_KEY, state.country);
          }
        }
        return cartId;
      } catch (e) {
        return createCart();
      }
    }

    /* 取运费选项。返回 { notShippable, groups, count }。
       groups 保留「发货组」结构（每组的选项数组），count 是选项总数。
       保留结构是为了 v7-d：结账页是「每组各选一个、然后相加」，
       一旦单行商品库存被拆到两个仓库（两个发货组），就必须求和而不是取最小。 */
    async function pollOptions(cartId) {
      for (var i = 0; i < 6; i++) {
        var data = await gql(cfg.apiUrl, cfg.token,
          'query($cid: ID!) { cart(id: $cid) { totalQuantity deliveryGroups(first: 10) { nodes { deliveryOptions { title estimatedCost { amount currencyCode } } } } } }',
          { cid: cartId });
        var cart = data.cart;
        if (!cart || cart.totalQuantity === 0) {
          return { notShippable: true, groups: [], count: 0 };
        }
        var groups = [];
        var count = 0;
        (cart.deliveryGroups.nodes || []).forEach(function (g) {
          var opts = g.deliveryOptions || [];
          if (opts.length) groups.push(opts);
          count += opts.length;
        });
        if (count) return { notShippable: false, groups: groups, count: count };
        await new Promise(function (res) { setTimeout(res, 400); });
      }
      return { notShippable: false, groups: [], count: 0 };
    }

    /* ---------- 主计算流程 ---------- */

    function currentKey() {
      return [state.variantId, state.qty, state.country].join('|');
    }

    async function runCalc(token) {
      var key = currentKey();

      var cached = readCache(key);
      if (cached) {
        if (state.token === token) { renderThreshold(); renderResult(cached); }
        return;
      }

      var c = COUNTRIES[state.country];
      if (!c) { if (state.token === token) showCannotShip(); return; }

      /* v7-b：可配送判定【不再有国家白名单】，完全交给接口。
         原因：同一国家能不能发随商品而变 —— 从 Warehouse-GZZF 发货的商品
         能发 AT/BE/DK/FI/NZ/JE/LT/VA/MO，从 Warehouse-HK海运 发货的不能发。
         写死国家名单必然造成「结账能下单、页面说不能发」的出入。 */

      /* 预检：商品在该国是否可售 → 不可售就直接给结论，不先闪出价格 */
      var sellable = await checkAvailability(state.country);
      if (state.token !== token) return;
      if (sellable === false) {
        if (window.console) {
          console.warn('[shipping-estimate] 商品在 ' + state.country +
            ' 市场不可售（未发布到该市场 / 无可用履约地点），已直接显示不可配送。' +
            '若该市场应当可售，请在 Shopify 后台检查该商品的「市场和产品发布」设置。');
        }
        showCannotShip();
        return;
      }

      /* 到这里已确认该市场可售（探测失败时保持乐观）→ 显示固定门槛行。
         放在预检之后，是为了"不可配送"的国家完全不出现门槛行 */
      renderThreshold();

      var cartId = await ensureCart();
      if (state.token !== token) return;
      if (!cartId) { showCheckoutNote(); return; }

      var r = await pollOptions(cartId);
      if (state.token !== token) return;
      if (r.notShippable) { showCannotShip(); return; }
      if (!r.count) { showCheckoutNote(); return; }

      /* v7-d：每个发货组各自取最低价后再求和，与结账页算法一致。
         （实测单行购物车即使 112 件、库存跨两个仓库，Shopify 也只返回 1 个组，
           所以这是防御性修正；正常情况结果与原来取 min 完全相同。） */
      var total = 0, currency = null;
      r.groups.forEach(function (g) {
        var cheapest = g.reduce(function (a, b) {
          return parseFloat(b.estimatedCost.amount) < parseFloat(a.estimatedCost.amount) ? b : a;
        });
        total += parseFloat(cheapest.estimatedCost.amount);
        currency = cheapest.estimatedCost.currencyCode;
      });
      var result = { amount: total, currency: currency };
      writeCache(key, result);
      renderResult(result);
    }

    var calc = debounce(function () { startCalc(); }, 200);

    function startCalc() {
      if (state.busy) { state.pending = true; return; } /* 不丢请求，跑完再补一次 */
      state.busy = true;

      var token = ++state.token;
      var settled = false;

      function finish() {
        state.busy = false;
        if (state.pending) {
          state.pending = false;
          startCalc();
        }
      }

      var timer = setTimeout(function () {
        if (settled) return;
        settled = true;
        state.token++; /* 让尚未返回的结果失效，不允许覆盖兜底文案 */
        if (window.console) {
          console.warn('[shipping-estimate] 计算超过 ' + (CALC_TIMEOUT_MS / 1000) +
            's 仍未返回，显示兜底文案：' + cfg.failNote);
        }
        showCheckoutNote();
        finish();
      }, CALC_TIMEOUT_MS);

      runCalc(token).catch(function (e) {
        if (window.console) console.warn('[shipping-estimate]', e);
        if (!settled && state.token === token) showCheckoutNote();
      }).then(function () {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        finish();
      });
    }

    /* 事件入口：命中缓存就直接渲染（不闪加载态），否则先给加载态再算 */
    function requestRecalc() {
      var cached = readCache(currentKey());
      if (cached) {
        renderThreshold();
        renderResult(cached);
      } else {
        showLoading();
        /* 该国的可售性已探测过且结论是「可售」→ 门槛行可立刻显示，不必等接口 */
        if (readCache(AVAIL_PREFIX + state.variantId + ':' + state.country) === true) {
          renderThreshold();
        }
      }
      calc();
    }

    /* ---------- 事件 ---------- */

    /* 变体切换（payload 里带 currentVariant.id） */
    document.addEventListener('maximize:variant-picker:updated:' + cfg.sectionId, function (e) {
      var cv = e.detail && e.detail.currentVariant;
      if (!cv || !cv.id) {
        el.classList.add('bse--hidden');
        return;
      }
      el.classList.remove('bse--hidden');
      state.variantId = String(cv.id);
      state.qty = readQty();
      requestRecalc();
    });

    /* 数量变化（数量输入框在 buy_buttons 区块内，用事件委托监听） */
    document.addEventListener('change', function (e) {
      if (!e.target || e.target.name !== 'quantity') return;
      var sec = e.target.closest('.section--' + cfg.sectionId);
      if (!sec) return;
      state.qty = readQty();
      requestRecalc();
    });

    /* 手动切换国家 */
    if (countrySel) {
      countrySel.addEventListener('change', function () {
        state.country = countrySel.value;
        ssSet(OVERRIDE_COUNTRY_KEY, state.country);
        renderCountry();
        requestRecalc();
      });
    }

    renderCountry();
    buildSelect();
    requestRecalc();
  }

  function boot(scope) {
    (scope || document).querySelectorAll('[data-shipping-estimate]').forEach(initBlock);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  /* 主题编辑器里预览/重载 section 时重新初始化 */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector) boot(e.target);
  });
})();
